<template>
    <component
        :is="tag"
        ref="targetEl"
        :style="tag === 'div' ? `min-height:${fixedMinHeight ? fixedMinHeight : minHeight}px` : null"
    >
        <slot v-if="shouldRender" />
    </component>
</template>

<script>
let mountCounter = 0; // Counter to know how many items were rendered
let resetTimeoutId = null;
let estimatedHeight = 0; // This is the height information for the first item to take, so we don't need to measure every time

export default {
    props: {
        tag: {
            type: String,
            default: 'div'
        },
        renderOnIdle: Boolean,
        unrender: Boolean,
        minHeight: Number,
        unrenderDelay: {
            type: Number,
            default: 10000,
        },
        disableLazy: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            shouldRender: false,
            targetEl: null,
            fixedMinHeight: 0,
            unrenderTimer: null,
            renderTimer: null,
            instanceId: 0,
            heightMeasurementTimer: null,
            observer: null,
        };
    },
    mounted() {
        if (this.disableLazy) {
            this.shouldRender = true;
            return;
        }

        this.instanceId = mountCounter++;

        // a way to clean shared states between batches of items
        clearTimeout(resetTimeoutId);
        resetTimeoutId = setTimeout(() => {
            mountCounter = 0;
            estimatedHeight = 0; // Reset estimated height for next batch
        }, 100);

        // Render the first 20 items immediately to avoid rendering all that have 0 height
        if (this.instanceId < 20) {
            this.shouldRender = true;

            // If this is the first item, measure its height after render and share it
            if (this.instanceId === 0) {
                this.$nextTick(() => {
                    // Assign targetEl before measuring
                    this.targetEl = this.$refs.targetEl;

                    // Wait a bit for content to fully render
                    this.heightMeasurementTimer = setTimeout(() => {
                        if (this.targetEl) {
                            const height = this.targetEl.getBoundingClientRect().height;
                            if (height > 0) {
                                estimatedHeight = Math.ceil(height);
                            }
                        }
                    }, 50);
                });
            }
            return;
        }

        this.targetEl = this.$refs.targetEl;

        // For items that will be lazy loaded, wait for estimated height if not available yet
        const setupLazyLoading = () => {
            // Use the estimated height from first item, or fall back to minHeight or 100
            this.fixedMinHeight = estimatedHeight || this.minHeight || 100;

            // Find the scroll container to use as root for IntersectionObserver
            const scrollContainer = this.findScrollContainer(this.targetEl);
            const root = scrollContainer === window ? null : scrollContainer;

            const options = {
                root: root,
                rootMargin: '200px',
            };

            this.observer = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) {
                    clearTimeout(this.unrenderTimer);
                    this.renderTimer = setTimeout(() => {
                        this.shouldRender = true;
                        // Clear fixedMinHeight after content renders to get the real size
                        this.$nextTick(() => {
                            this.fixedMinHeight = 0;
                        });
                    }, this.unrender ? 200 : 0);
                    if (!this.unrender) {
                        this.observer.disconnect();
                    }
                } else if (this.unrender) {
                    clearTimeout(this.renderTimer);
                    this.unrenderTimer = setTimeout(() => {
                        const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

                        // Get dimensions before unrendering
                        const clientHeight = this.targetEl.clientHeight || 0;
                        const scrollHeight = this.targetEl.scrollHeight || 0;

                        this.fixedMinHeight = Math.max(
                            this.minHeight || clientHeight,
                            scrollHeight
                        );

                        this.shouldRender = false;

                        // Restore scroll position if we found a scroll container
                        if (scrollContainer) {
                            this.$nextTick(() => {
                                scrollContainer.scrollTop = scrollTop;
                            });
                        }
                    }, this.unrenderDelay);
                }
            }, options);

            // Start observing after DOM settles
            this.$nextTick(() => {
                this.observer.observe(this.targetEl);
            });

            if (this.renderOnIdle) {
                this.onIdle(() => {
                    this.shouldRender = true;
                    if (!this.unrender && this.observer) {
                        this.observer.disconnect();
                    }
                });
            }
        };

        // If estimatedHeight is already available or we have minHeight, set up immediately
        // Otherwise, wait a bit for the first item to measure (max 100ms)
        if (estimatedHeight || this.minHeight) {
            setupLazyLoading();
        } else {
            // Wait for estimated height with a timeout
            const maxWaitTime = 100;
            const startTime = Date.now();
            const waitForEstimate = () => {
                if (estimatedHeight || Date.now() - startTime >= maxWaitTime) {
                    setupLazyLoading();
                } else {
                    requestAnimationFrame(waitForEstimate);
                }
            };
            waitForEstimate();
        }
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        clearTimeout(this.renderTimer);
        clearTimeout(this.unrenderTimer);
        clearTimeout(this.heightMeasurementTimer);

        // Clear the global reset timeout if this is the last component being destroyed
        if (resetTimeoutId) {
            clearTimeout(resetTimeoutId);
            resetTimeoutId = null;
        }
    },
    methods: {
        onIdle(cb = () => { }) {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(cb);
            } else {
                setTimeout(() => {
                    this.$nextTick(cb);
                }, 300);
            }
        },
        findScrollContainer(element) {
            if (!element || element === document.documentElement) {
                return window;
            }

            let parent = element.parentElement;
            while (parent) {
                const { overflow, overflowY } = window.getComputedStyle(parent);
                if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
                    return parent;
                }
                parent = parent.parentElement;
            }

            return window;
        },
    },
};
</script>
